import { Box, Paper, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import LoadingDialog from "../../components/Common/LoadingDialog";
import Notification from "../../components/Common/Notification";
import CustomButton from "../../components/Form/CustomButton";
import CustomStepper from "../../components/Form/CustomStepper";
import { createQuotation, getGeneral, resetQuotationActionState, searchGeneral } from "../../store/actions";
import CustomerDetails from "./Form/CustomerDetails";
import InstalmentCalculation from "./Form/InstalmentCalculation";
import VehicleDetails from "./Form/VehicleDetails";
import Helpers from "../../helpers/helpers";

const steps = ["Vehicle details", "Instalment calculation", "Customer details"];

const optionalValue = value => value === "" || value === null || value === undefined ? null : value;
const numberValue = value => Number(value || 0);

const AddQuotation = ({ makeList, loanInterestRatesList, leftSideBarType, quotationAdd, quotationCalculation, quotationError, isEdit = false, initialFormObj, isEditSubmitting = false, onEditSubmit, onEditActionChange, onEditClose, isDirectSeller }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingStep, setPendingStep] = useState(null);
  const [notification, setNotification] = useState({ open: false, msg: "", type: "success" });
  const [formObj, setFormObj] = useState({
    vehicleSource: "manual",
    bike_advert_type: "",
    make: "",
    model: "",
    registrationNo: "",
    year: "",
    relatedLink: "",
    vehicle: "",
    price: "",
    overtrade: "",
    coe: "",
    insurance: "",
    fee: "",
    other: "",
    downpayment: "",
    term: "",
    rate: "",
    customer_name: "",
    customer_email: "",
    phone: "",
    remarks: "",
    ...(isEdit ? initialFormObj : {}),
  });

  useEffect(() => {
    if (isDirectSeller) navigate("/not-found");
  }, [isDirectSeller, navigate]);

  useEffect(() => {
    if (isEdit) return undefined;
    dispatch(resetQuotationActionState());
    dispatch(getGeneral());
    dispatch(searchGeneral({ type: "bike_makes", search: "" }));
    return () => dispatch(resetQuotationActionState());
  }, [dispatch, isEdit]);
  
  useEffect(() => {
    // if edit, only when select new vehicle id from listing / inventory need to prefill from api
    if (!isSubmitting || (isEdit && pendingStep > 1) || (initialFormObj?.vehicle?.id === formObj?.vehicle?.id) ) return;
    if (quotationAdd?.code === "validate_success" && (pendingStep === 1 || pendingStep === 2)) {
      if (pendingStep === 1 && quotationAdd?.data) {
        // if returned loan_term not exist in options then no need to fill in
        const loanTermExist = Boolean(Helpers.loanTermOptions?.find((item) => item.value === quotationAdd.data?.loan_term));
        const interestRateExist = Boolean(loanInterestRatesList?.find((item) => item.id === quotationAdd.data?.interest_rate_id))
        setFormObj({
          ...formObj,
          price: quotationAdd.data?.machine_price,
          coe: quotationAdd.data?.coe,
          downpayment: quotationAdd.data?.downpayment,
          term: loanTermExist ? quotationAdd.data?.loan_term : null,
          interest_rate_id: interestRateExist && isEdit ? quotationAdd.data?.interest_rate_id : null,
          rate: interestRateExist && !isEdit ? loanInterestRatesList?.find((item) => item.id === quotationAdd.data?.interest_rate_id)?.interest_rate : null
        })
      }
      setIsSubmitting(false);
      setPendingStep(null);
      setActiveStep(pendingStep);
      dispatch(resetQuotationActionState());
      return;
    }
    if (quotationAdd?.code === "success" && pendingStep === "submit") {
      setIsSubmitting(false);
      setPendingStep(null);
      const successMessage = quotationAdd?.message || "Quotation created successfully.";
      dispatch(resetQuotationActionState());
      navigate("/quotations", {
        state: {
          quotationNotification: {
            open: true,
            msg: successMessage,
            type: "success",
          },
        },
      });
    }
  }, [dispatch, isEdit, isSubmitting, navigate, pendingStep, quotationAdd]);

  useEffect(() => {
    if (!isSubmitting || !quotationError || (Array.isArray(quotationError) && quotationError.length === 0)) return;
    setIsSubmitting(false);
    setPendingStep(null);
    setNotification({
      open: true,
      msg: quotationError?.message || quotationError?.[0] || "Unable to create quotation. Please try again.",
      type: "error",
    });
  }, [isSubmitting, quotationError]);

  const handleFormObjChange = (field, value) =>
    setFormObj((current) => ({ ...current, [field]: value }));

  const buildPayload = useCallback((step) => {
    const vehiclePayload = {
      step,
      vehicle_source: formObj.vehicleSource,
      bike_advert_type: formObj.bike_advert_type?.charAt(0).toLowerCase() + formObj.bike_advert_type?.slice(1) || null,
    };

    if (formObj.vehicleSource === "manual") {
      vehiclePayload.bike_make_id = formObj.make;
      vehiclePayload.bike_model_id = formObj.model;
      vehiclePayload.register_number = optionalValue(formObj.registrationNo);
      vehiclePayload.manu_year = optionalValue(formObj.year) === null ? null : Number(formObj.year);
    } else if (formObj.vehicleSource === "listing") {
      vehiclePayload.bike_advert_id = formObj.vehicle?.id || null;
      vehiclePayload.related_link = optionalValue(formObj.relatedLink);
    } else {
      vehiclePayload.inventory_id = formObj.vehicle?.id || null;
      vehiclePayload.related_link = null;
    }

    if (step === 1) return vehiclePayload;

    const pricingPayload = {
      ...vehiclePayload,
      related_link: formObj.vehicleSource === "inventory" ? null : optionalValue(formObj.relatedLink),
      machine_price: numberValue(formObj.price),
      overtrade: numberValue(formObj.overtrade),
      coe: numberValue(formObj.coe),
      insurance: numberValue(formObj.insurance),
      admin_fee: numberValue(formObj.fee),
      other_charges: numberValue(formObj.other),
      downpayment: numberValue(formObj.downpayment),
      loan_term: Number(formObj.term),
      interest_rate: Number(formObj.rate),
    };

    if (step === 2) return pricingPayload;

    return {
      ...pricingPayload,
      customer_name: formObj.customer_name.trim(),
      customer_phone: optionalValue(formObj.phone ? (String(formObj.phone).startsWith("+") ? formObj.phone : `+65${formObj.phone}`) : ""),
      customer_email: optionalValue(formObj.customer_email?.trim()),
      remarks: optionalValue(formObj.remarks?.trim()),
    };
  }, [formObj, isEdit]);

  const stepContent = [
    <VehicleDetails isEdit={isEdit} key="vehicle" formObj={formObj} handleFormObjChange={handleFormObjChange} makeList={makeList} error={quotationError} />,
    <InstalmentCalculation isEdit={isEdit} key="calculation" formObj={formObj} handleFormObjChange={handleFormObjChange} />,
    <CustomerDetails isEdit={isEdit} key="customer" formObj={formObj} handleFormObjChange={handleFormObjChange} error={quotationError} />,
  ];

  const handleBack = useCallback(() => {
    if (activeStep === 0) {
      if (isEdit) onEditClose?.();
      else navigate("/quotations");
    }
    else setActiveStep((step) => step - 1);
  }, [activeStep, isEdit, navigate, onEditClose]);

  const handleNext = useCallback(() => {
    if (isEdit && ((initialFormObj?.vehicle?.id === formObj?.vehicle?.id )|| activeStep > 0 || formObj?.vehicleSource === 'manual')) {
      if (activeStep === steps.length - 1) {
        onEditSubmit?.(buildPayload("submit"));
      } else {
        setActiveStep((step) => step + 1);
      }
      return;
    }

    const requestStep = activeStep === steps.length - 1 ? "submit" : activeStep + 1;
    setPendingStep(requestStep);
    setIsSubmitting(true);
    dispatch(resetQuotationActionState());
    dispatch(createQuotation(buildPayload(requestStep)));
  }, [activeStep, buildPayload, dispatch, isEdit, onEditSubmit]);

  const actionTitle = activeStep === steps.length - 1
    ? (isEdit ? "Save changes" : "Save quotation")
    : `Next: ${steps[activeStep + 1]}`;
  const isCalculationRequired = activeStep === 1 && quotationCalculation?.code !== "success";
  const isNextDisabled = isSubmitting || isCalculationRequired;
  const isEditNextDisabled = isEditSubmitting || isCalculationRequired;

  useEffect(() => {
    if (isEdit) {
      onEditActionChange?.(
        <Stack direction="row" justifyContent="space-between" width="100%">
          <CustomButton
            title={activeStep === 0 ? "Close" : "Go back"}
            press={handleBack}
            backgroundColor="#FFFFFF"
            textColor="#1D1B1B"
            borderStyle="1px solid #1D1B1B"
            height={40}
            paddingInline={20}
          />
          <CustomButton
            title={actionTitle}
            press={handleNext}
            disabled={isEditNextDisabled}
            height={40}
            paddingInline={22}
            backgroundColor={isEditNextDisabled ? "#D9D9D9" : "#1D1B1B"}
            textColor={isEditNextDisabled ? "#808588" : "#FFFFFF"}
            borderStyle={isEditNextDisabled ? "1px solid #D9D9D9" : "1px solid #1D1B1B"}
          />
        </Stack>,
      );
    }
  }, [actionTitle, activeStep, handleBack, handleNext, isEdit, isEditNextDisabled, onEditActionChange]);

  return (
    <div className={!isEdit ? "page-content" : ""}>
      <LoadingDialog open={isSubmitting || isEditSubmitting} />
      <Notification
        notification={notification}
        setNotification={setNotification}
        errors={quotationError?.errors}
        handleClosed={() => setNotification((current) => ({ ...current, open: false }))}
      />
      <Container fluid>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box px={{ xs: 0, sm: 1 }}>
            {!isEdit && <>
              <Typography fontSize={22} fontWeight={600}>Quotations</Typography>
              <Typography fontSize={14} color="#808588" mt={1}>
                Estimate monthly instalments for any vehicle configuration
              </Typography>
            </>}
            <Box sx={{ width: { xs: "100%", md: 610 }, mt: isEdit ? 2 : 3, mb: 4 }}>
              <CustomStepper steps={steps} activeStep={activeStep} />
            </Box>

            <Box pb={12}>{stepContent[activeStep]}</Box>
          </Box>

          {!isEdit && <Paper
            elevation={0}
            square
            sx={{
              position: "fixed",
              bottom: 0,
              left: {
                xs: 0,
                md: leftSideBarType === "condensed" ? "70px" : "280px",
              },
              right: 0,
              px: { xs: 2, sm: 5 },
              py: 2.25,
              borderTop: "1px solid #E6E6E6",
              backgroundColor: "#FFFFFF",
              zIndex: 10,
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <CustomButton title="Go back" press={handleBack} backgroundColor="#FFFFFF" textColor="#1D1B1B" borderStyle="1px solid #1D1B1B" height={40} paddingInline={20} />
              <CustomButton
                title={actionTitle}
                press={handleNext}
                disabled={isNextDisabled}
                height={40}
                paddingInline={22}
                backgroundColor={isNextDisabled ? "#D9D9D9" : "#1D1B1B"}
                textColor={isNextDisabled ? "#808588" : "#FFFFFF"}
                borderStyle={isNextDisabled ? "1px solid #D9D9D9" : "1px solid #1D1B1B"}
              />
            </Stack>
          </Paper>}
        </Box>
      </Container>
    </div>
  );
};

const mapStateToProps = ({ General, Layout, Login, Quotation }, ownProps) => ({
  makeList: ownProps.makeList?.length ? ownProps.makeList : (General.bike_make_search?.length ? General.bike_make_search : General.bike_makes) || [],
  leftSideBarType: Layout.leftSideBarType,
  isDirectSeller: Login?.verifyAdmin?.user?.data?.profile?.selected_dealer_id ? false : true,
  quotationAdd: Quotation.quotationAdd,
  quotationCalculation: Quotation.quotationCalculation,
  quotationError: Quotation.error,
  loanInterestRatesList: General.loan_interest_rates,
});

export default connect(mapStateToProps)(AddQuotation);
